/**
 * Solar module grid placement calculator
 * Complex algorithm for optimal module placement within roof polygons
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 4550-4749)
 * to provide reusable, testable module grid generation.
 */

import {
  PolygonCoordinates,
  calculatePolygonArea,
  isPointInPolygon,
} from '../geometry';
import {
  ModuleSpec,
  GridOptions,
  PlacedModule,
  GridResult,
  ModuleOrientation,
} from './types';

/**
 * Default module specification (Canadian Solar 450W)
 */
export const DEFAULT_MODULE_SPEC: ModuleSpec = {
  width: 1.134, // meters
  height: 2.278, // meters
  power: 450, // watts
  brand: 'Canadian Solar',
  model: 'CS7N-450MS',
};

/**
 * Default grid options
 */
export const DEFAULT_GRID_OPTIONS: GridOptions = {
  spacing: 0.02, // 2cm between modules
  rowSpacing: 1.5, // 1.5x module height for row spacing
  orientation: 'portrait',
  rotation: 0,
  targetCount: null, // no limit
};

/**
 * Calculate module grid for a bounding box within a roof polygon
 *
 * This algorithm:
 * 1. Works in rotated coordinate space aligned with module orientation
 * 2. Places modules in a top-left aligned grid pattern
 * 3. Applies setback from segment edges (0.3m)
 * 4. Checks containment within both box and polygon
 * 5. Avoids obstructions with clearance distance
 * 6. Optionally limits to target module count
 *
 * @param box - Bounding box coordinates for module placement
 * @param polygon - Roof polygon coordinates (modules must be inside)
 * @param obstructions - Array of obstruction polygons to avoid
 * @param moduleSpec - Module dimensions and specifications
 * @param options - Grid placement options
 * @returns Array of placed modules with positions and metadata
 */
export function calculateModuleGrid(
  box: PolygonCoordinates,
  polygon: PolygonCoordinates,
  obstructions: PolygonCoordinates[],
  moduleSpec: ModuleSpec = DEFAULT_MODULE_SPEC,
  options: GridOptions = DEFAULT_GRID_OPTIONS
): PlacedModule[] {
  if (box.length < 3) return [];

  const { width: moduleWidth, height: moduleHeight } = moduleSpec;
  const {
    spacing,
    rowSpacing,
    orientation,
    rotation,
    targetCount,
  } = options;

  // Calculate box center for coordinate transformations
  const boxLats = box.map(([lat]) => lat);
  const boxLngs = box.map(([, lng]) => lng);
  const centerLat = boxLats.reduce((a, b) => a + b, 0) / boxLats.length;
  const centerLng = boxLngs.reduce((a, b) => a + b, 0) / boxLngs.length;

  // Coordinate conversion factors
  const latDegPerMeter = 1 / 111000;
  const lngDegPerMeter = 1 / (111000 * Math.cos((centerLat * Math.PI) / 180));

  // Calculate effective dimensions based on orientation
  const effectiveWidth = orientation === 'landscape' ? moduleHeight : moduleWidth;
  const effectiveHeight = orientation === 'landscape' ? moduleWidth : moduleHeight;

  // Row spacing distance
  const rowSpacingDistance = effectiveHeight * rowSpacing;

  // Setup rotation transformations
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  // Transform point from rotated space to lat/lng
  const transformToLatLng = (rotX: number, rotY: number): [number, number] => {
    const lat = centerLat + (rotX * cos - rotY * sin);
    const lng = centerLng + (rotX * sin + rotY * cos);
    return [lat, lng];
  };

  // Transform point from lat/lng to rotated space
  const transformToRotated = (lat: number, lng: number): [number, number] => {
    const deltaLat = lat - centerLat;
    const deltaLng = lng - centerLng;
    const rotX = deltaLat * cos + deltaLng * sin;
    const rotY = -deltaLat * sin + deltaLng * cos;
    return [rotX, rotY];
  };

  // Transform box to rotated space to find bounds
  const rotatedBox = box.map(([lat, lng]) => transformToRotated(lat, lng));
  const minRotX = Math.min(...rotatedBox.map(([x]) => x));
  const maxRotX = Math.max(...rotatedBox.map(([x]) => x));
  const minRotY = Math.min(...rotatedBox.map(([, y]) => y));
  const maxRotY = Math.max(...rotatedBox.map(([, y]) => y));

  // Apply segment edge setback (0.3 meters from segment boundaries)
  const segmentEdgeSetback = 0.3; // meters
  const setbackLatDeg = segmentEdgeSetback * latDegPerMeter;
  const setbackLngDeg = segmentEdgeSetback * lngDegPerMeter;

  const workingMinRotX = minRotX + setbackLatDeg;
  const workingMaxRotX = maxRotX - setbackLatDeg;
  const workingMinRotY = minRotY + setbackLngDeg;
  const workingMaxRotY = maxRotY - setbackLngDeg;

  // Calculate module dimensions in rotated space
  const moduleRotXSize = effectiveHeight * latDegPerMeter;
  const moduleRotYSize = effectiveWidth * lngDegPerMeter;
  const moduleSpacingRotY = spacing * lngDegPerMeter;
  const rowSpacingRotX = rowSpacingDistance * latDegPerMeter;

  // Start placing modules from top-left corner (after setback)
  const startRotX = workingMinRotX + (effectiveHeight / 2) * latDegPerMeter;
  const startRotY = workingMinRotY + (effectiveWidth / 2) * lngDegPerMeter;

  const modules: PlacedModule[] = [];

  // Place modules in top-left aligned grid
  let currentRotX = startRotX;

  while (currentRotX + (effectiveHeight / 2) * latDegPerMeter <= workingMaxRotX) {
    let currentRotY = startRotY;

    while (currentRotY + (effectiveWidth / 2) * lngDegPerMeter <= workingMaxRotY) {
      // Transform back to lat/lng
      const [lat, lng] = transformToLatLng(currentRotX, currentRotY);
      const modulePosition: [number, number] = [lat, lng];

      // Create module rectangle for collision checking
      const moduleRect = createModuleRectangle(
        modulePosition,
        moduleWidth,
        moduleHeight,
        orientation,
        rotation
      );

      // Check if module is inside both box and polygon
      const moduleCenterInside = isPointInPolygon(modulePosition, polygon);

      const allCornersInside = moduleRect.slice(0, -1).every(
        (corner) =>
          isPointInPolygon(corner, polygon) && isPointInPolygon(corner, box)
      );

      if (moduleCenterInside && allCornersInside) {
        // Check for collision with obstructions
        const overlapsObstruction = checkModuleObstructionCollision(
          modulePosition,
          moduleRect,
          obstructions,
          latDegPerMeter,
          lngDegPerMeter
        );

        if (!overlapsObstruction) {
          // Add module to grid
          modules.push({
            position: modulePosition,
            width: moduleWidth,
            height: moduleHeight,
            orientation,
            rotation,
            azimuth: rotation, // Face azimuth same as rotation
            moduleBrand: moduleSpec.brand,
            moduleModel: moduleSpec.model,
            modulePowerWatts: moduleSpec.power,
          });
        }
      }

      currentRotY += (effectiveWidth + spacing) * lngDegPerMeter;
    }

    currentRotX += rowSpacingDistance * latDegPerMeter;
  }

  // Apply target module count constraint if specified
  if (targetCount !== null && targetCount !== undefined) {
    return modules.slice(0, targetCount);
  }

  return modules;
}

/**
 * Create a rectangle for a solar module
 * Returns 5 points (closed polygon) representing module corners
 *
 * @param center - Center position [lat, lng]
 * @param width - Module width in meters
 * @param height - Module height in meters
 * @param orientation - Portrait or landscape
 * @param rotation - Rotation angle in degrees
 * @returns Closed rectangle polygon
 */
export function createModuleRectangle(
  center: [number, number],
  width: number,
  height: number,
  orientation: ModuleOrientation,
  rotation: number = 0
): PolygonCoordinates {
  const [lat, lng] = center;
  const latDegPerMeter = 1 / 111000;
  const lngDegPerMeter = 1 / (111000 * Math.cos((lat * Math.PI) / 180));

  // Swap dimensions for landscape
  const effectiveWidth = orientation === 'landscape' ? height : width;
  const effectiveHeight = orientation === 'landscape' ? width : height;

  const halfWidth = effectiveWidth / 2;
  const halfHeight = effectiveHeight / 2;

  // Create unrotated rectangle
  let rectangle: PolygonCoordinates = [
    [lat + halfHeight * latDegPerMeter, lng - halfWidth * lngDegPerMeter], // top-left
    [lat + halfHeight * latDegPerMeter, lng + halfWidth * lngDegPerMeter], // top-right
    [lat - halfHeight * latDegPerMeter, lng + halfWidth * lngDegPerMeter], // bottom-right
    [lat - halfHeight * latDegPerMeter, lng - halfWidth * lngDegPerMeter], // bottom-left
    [lat + halfHeight * latDegPerMeter, lng - halfWidth * lngDegPerMeter], // close
  ];

  // Apply rotation if specified
  // IMPORTANT: Rotation must be done in meter-space, not degree-space,
  // because lat and lng degrees represent different distances
  if (rotation !== 0) {
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    rectangle = rectangle.map(([pointLat, pointLng]) => {
      // Convert delta from degrees to meters
      const deltaLatMeters = (pointLat - lat) / latDegPerMeter;
      const deltaLngMeters = (pointLng - lng) / lngDegPerMeter;

      // Rotate in meter-space (consistent units)
      const rotatedLatMeters = deltaLatMeters * cos - deltaLngMeters * sin;
      const rotatedLngMeters = deltaLatMeters * sin + deltaLngMeters * cos;

      // Convert back to degrees
      const newLat = lat + rotatedLatMeters * latDegPerMeter;
      const newLng = lng + rotatedLngMeters * lngDegPerMeter;
      return [newLat, newLng] as [number, number];
    });
  }

  return rectangle;
}

/**
 * Check if a module collides with any obstructions
 * Uses multiple collision detection methods for accuracy
 *
 * @param modulePosition - Center of module
 * @param moduleRect - Module rectangle corners
 * @param obstructions - Array of obstruction polygons
 * @param latDegPerMeter - Latitude conversion factor
 * @param lngDegPerMeter - Longitude conversion factor
 * @returns True if module overlaps any obstruction
 */
function checkModuleObstructionCollision(
  modulePosition: [number, number],
  moduleRect: PolygonCoordinates,
  obstructions: PolygonCoordinates[],
  latDegPerMeter: number,
  lngDegPerMeter: number
): boolean {
  const obstructionSetback = 0.3; // meters clearance required

  for (const obstruction of obstructions) {
    // Check 1: Module center inside obstruction
    if (isPointInPolygon(modulePosition, obstruction)) {
      return true;
    }

    // Check 2: Any module corner inside obstruction
    const moduleCornersInside = moduleRect
      .slice(0, -1)
      .some((corner) => isPointInPolygon(corner, obstruction));
    if (moduleCornersInside) {
      return true;
    }

    // Check 3: Any obstruction corner inside module
    const obstructionCornersInside = obstruction
      .slice(0, -1)
      .some((corner) => isPointInPolygon(corner, moduleRect.slice(0, -1)));
    if (obstructionCornersInside) {
      return true;
    }

    // Check 4: Clearance distance (proximity check)
    for (let i = 0; i < moduleRect.length - 1; i++) {
      const [modLat, modLng] = moduleRect[i];

      for (let j = 0; j < obstruction.length - 1; j++) {
        const [obsLat, obsLng] = obstruction[j];
        const distMeters = Math.sqrt(
          Math.pow((modLat - obsLat) / latDegPerMeter, 2) +
            Math.pow((modLng - obsLng) / lngDegPerMeter, 2)
        );

        if (distMeters < obstructionSetback) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Calculate comprehensive grid result with statistics
 *
 * @param modules - Array of placed modules
 * @param moduleSpec - Module specifications
 * @param polygonArea - Total roof polygon area (optional)
 * @returns Grid result with statistics
 */
export function calculateGridResult(
  modules: PlacedModule[],
  moduleSpec: ModuleSpec,
  polygonArea?: number
): GridResult {
  const count = modules.length;
  const moduleArea = moduleSpec.width * moduleSpec.height;
  const totalArea = count * moduleArea;
  const capacity = (count * moduleSpec.power) / 1000; // kW

  const coverage = polygonArea ? (totalArea / polygonArea) * 100 : 0;

  return {
    modules,
    count,
    totalArea,
    coverage,
    capacity,
  };
}

/**
 * Optimize module layout by trying different rotations
 * Finds the rotation that maximizes module count
 *
 * @param box - Bounding box for placement
 * @param polygon - Roof polygon
 * @param obstructions - Obstructions to avoid
 * @param moduleSpec - Module specifications
 * @param baseOptions - Base grid options
 * @returns Optimized grid result with best rotation
 */
export function optimizeModuleLayout(
  box: PolygonCoordinates,
  polygon: PolygonCoordinates,
  obstructions: PolygonCoordinates[],
  moduleSpec: ModuleSpec = DEFAULT_MODULE_SPEC,
  baseOptions: GridOptions = DEFAULT_GRID_OPTIONS
): GridResult & { optimalRotation: number } {
  const rotationsToTry = [0, 45, 90, 135, 180, 225, 270, 315];
  let bestResult: GridResult | null = null;
  let optimalRotation = 0;

  for (const rotation of rotationsToTry) {
    const options = { ...baseOptions, rotation };
    const modules = calculateModuleGrid(
      box,
      polygon,
      obstructions,
      moduleSpec,
      options
    );

    const result = calculateGridResult(modules, moduleSpec);

    if (!bestResult || result.count > bestResult.count) {
      bestResult = result;
      optimalRotation = rotation;
    }
  }

  return {
    ...(bestResult || calculateGridResult([], moduleSpec)),
    optimalRotation,
  };
}
